import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ClipboardList } from 'lucide-react';

// Admin page to manage GENERAL (non-equipment) shop kiosk to-do items.
// Equipment tasks go through Equipment Issues, not here. Gated by 'shop-todo'
// page access (super admin or grant on the Admin Users page).

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface TodoItem {
  todo_id: number;
  title: string;
  priority: 'low' | 'normal' | 'high';
  is_done: boolean;
  created_at: string;
  created_by: string | null;
}

const priorityColor = (p: string) => (p === 'high' ? '#dc2626' : p === 'low' ? '#94a3b8' : '#2563eb');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('bfa_token')}`,
});

export const KioskTodoAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/kiosk-todo`, { headers: authHeaders() });
      const json = await res.json();
      if (json.success) setItems(json.data);
    } catch { setError('Failed to load to-do items'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/kiosk-todo`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title: title.trim(), priority }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed');
      setTitle('');
      setPriority('normal');
      await load();
    } catch (e: any) { setError(e.message || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/kiosk-todo/${id}`, { method: 'DELETE', headers: authHeaders() });
      setItems(prev => prev.filter(i => i.todo_id !== id));
    } catch { setError('Failed to delete'); }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button onClick={() => navigate('/')} style={s.backBtn} aria-label="Back"><ArrowLeft size={22} /></button>
        <ClipboardList size={22} />
        <h1 style={s.title}>Shop To-Do</h1>
      </div>
      <p style={s.subtitle}>General shop tasks shown on the kiosk. For a machine-specific task, use Equipment Issues instead.</p>

      <div style={s.addCard}>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="New task (e.g. Order shop rags, clean welding area)…"
          style={s.input}
        />
        <div style={s.addRow}>
          <select value={priority} onChange={e => setPriority(e.target.value as any)} style={s.select}>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          <button onClick={add} disabled={saving || !title.trim()} style={{ ...s.addBtn, opacity: saving || !title.trim() ? 0.5 : 1 }}>
            <Plus size={18} style={{ marginRight: 6 }} />{saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {loading ? <div style={s.note}>Loading…</div>
        : items.length === 0 ? <div style={s.note}>No open items. Add one above.</div>
        : items.map(i => (
          <div key={i.todo_id} style={{ ...s.item, borderLeft: `5px solid ${priorityColor(i.priority)}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.itemTitle}>{i.title}</div>
              <div style={s.itemMeta}>
                <span style={{ ...s.prioTag, background: priorityColor(i.priority) }}>{i.priority.toUpperCase()}</span>
                {i.created_by && <span style={s.muted}>added by {i.created_by}</span>}
              </div>
            </div>
            <button onClick={() => remove(i.todo_id)} style={s.delBtn} aria-label="Delete"><Trash2 size={18} /></button>
          </div>
        ))}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 720, margin: '0 auto', padding: 16, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  backBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', color: '#475569' },
  title: { fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a' },
  subtitle: { fontSize: 14, color: '#64748b', margin: '4px 0 16px' },
  addCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 16 },
  input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 16, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 10 },
  addRow: { display: 'flex', gap: 10 },
  select: { padding: '10px 12px', fontSize: 15, border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff' },
  addBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '10px 16px', fontSize: 16, fontWeight: 700, color: '#fff', background: '#2563eb', border: 'none', borderRadius: 8, cursor: 'pointer' },
  error: { background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 14, fontWeight: 600, marginBottom: 12 },
  note: { textAlign: 'center', color: '#94a3b8', fontSize: 15, fontWeight: 600, padding: 24 },
  item: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 8 },
  itemTitle: { fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 },
  itemMeta: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  prioTag: { display: 'inline-flex', alignItems: 'center', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: 0.3, padding: '2px 7px', borderRadius: 5 },
  muted: { fontSize: 12, color: '#94a3b8', fontWeight: 600 },
  delBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, border: '1px solid #fecaca', borderRadius: 10, background: '#fff', color: '#dc2626', cursor: 'pointer', flexShrink: 0 },
};

export default KioskTodoAdmin;
